import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Antenna, Bell, Building, CreditCard, Gamepad2, GraduationCap, Headset, Key, KeyRound, Lock, Mail, Megaphone, Menu, MessageSquare, PhoneCall, Receipt, Route, ShieldAlert, ShieldCheck, ShoppingCart, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import yootelcoLogo from "@/assets/yootelco-logo.png.asset.json";

const navItems = [
  { label: "Solutions", hasChevron: true },
  { label: "Use Cases", hasChevron: true },
  { label: "Industries", hasChevron: true },
  { label: "Coverage & Routes", hasChevron: false },
  { label: "About", hasChevron: false },
  { label: "Blog", hasChevron: false, externalHref: "https://blog.yootelco.com/" },
];

const solutionsMenu = [
  { title: "A2P SMS", description: "Scalable messaging solutions for global delivery and enterprise use cases.", Icon: MessageSquare, href: "/solutions/a2p-sms" },
  { title: "OTP & Authentication", description: "Secure user verification and fraud prevention with reliable OTP delivery.", Icon: ShieldCheck, href: "/solutions/otp-authentication" },
  { title: "SIP Trunking & Wholesale Voice", description: "Carrier-grade SIP trunking and wholesale voice termination at scale.", Icon: PhoneCall, href: "/solutions/voice-termination" },
  { title: "Call Center Solutions", description: "Professional inbound and outbound call center services for customer support and sales.", Icon: Headset, href: "/solutions/call-center-solutions" },
];

const useCasesMenu = [
  { title: "OTP Verification", description: "Send one-time codes that arrive quickly, so people can verify without frustration.", Icon: ShieldCheck, href: "/use-cases/otp-verification" },
  { title: "Transactional Alerts", description: "Let customers know what's happening with their order — confirmations, shipping, and account updates, right when they need them.", Icon: Bell, href: "/use-cases/transactional-alerts" },
  { title: "Platform Notifications", description: "Simple SMS updates that help people stay connected to your platform.", Icon: Route, href: "/use-cases/platform-notifications" },
  { title: "Payment & Fintech", description: "Messaging that keeps transactions, fraud alerts, and account activity secure and clear.", Icon: CreditCard, href: "/use-cases/payment-fintech" },
  { title: "User Verification", description: "A quick, reliable way to confirm someone is who they say they are.", Icon: UserCheck, href: "/solutions/user-verification" },
  { title: "Two-Factor Authentication", description: "An extra layer of protection for logins, without making things harder for your users.", Icon: KeyRound, href: "/solutions/two-factor-authentication" },

  { title: "One-Time Password (OTP)", description: "Deliver time-sensitive OTPs with high success rates globally.", Icon: Key, href: "/solutions/otp-authentication" },
  { title: "Marketing Campaigns", description: "Reach customers with personalized promotional messages at scale.", Icon: Megaphone, href: "/use-cases/promotional-sms" },
  { title: "Transaction Alerts", description: "Notify customers instantly about payments, transfers, and activity.", Icon: Receipt, href: "/use-cases/transactional-alerts" },
  { title: "Customer Notifications", description: "Keep customers informed with order and service updates.", Icon: Mail, href: "/use-cases/platform-notifications" },
  { title: "Fraud Prevention", description: "Detect suspicious activity and alert users in real time.", Icon: ShieldAlert, href: "/use-cases/fraud-prevention" },
  { title: "Account Security", description: "Protect accounts with secure login alerts and verification.", Icon: Lock, href: "/solutions/two-factor-authentication" },
];

const industriesMenu = [
  { title: "Fintech & Payments", description: "Secure OTP delivery and timely transaction alerts for platforms that can't afford to get this wrong.", Icon: CreditCard, href: "/industries/fintech-payments" },
  { title: "Digital Marketing Agencies", description: "Tools that help agencies run campaigns for multiple clients without the delivery headaches.", Icon: Megaphone, href: "/industries/digital-marketing-agencies" },
  { title: "iGaming", description: "Fast player verification and messaging built for a platform that never really closes.", Icon: Gamepad2, href: "/industries/igaming" },
  { title: "Telecom & CPaaS", description: "Reliable messaging and voice connectivity for partners building on top of our network.", Icon: Antenna, href: "/industries/telecom-cpaas" },
  { title: "Retail & Ecommerce", description: "Promotions and order updates that reach customers when it actually matters.", Icon: ShoppingCart, href: "/industries/retail-ecommerce" },
  { title: "Real Estate", description: "Stay in touch with leads and clients through automated, timely communication.", Icon: Building, href: "/industries/real-estate" },

  { title: "Education Marketing", description: "Student engagement messaging and application status alerts.", Icon: GraduationCap, href: "/industries/education-marketing" },
];


const slug = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const navHref = (label: string) => {
  if (label === "Industries") return "/industries";
  if (label === "Coverage & Routes") return "/coverage-routes";
  if (label === "About") return "/about";
  return `/#${slug(label)}`;
};

const SiteHeader = () => {
  return (
    <header className="fixed left-0 top-0 z-[1000] w-full bg-transparent transition-shadow duration-300">
      <div className="container pt-4 md:pt-5">
        <div className="hero-nav mx-auto grid max-w-[1320px] grid-cols-[auto_1fr_auto] items-center gap-6 px-4 py-[12px] md:px-6 lg:px-8">
          <Link to="/" aria-label="Yootelco home" className="flex items-center">
            <img src={yootelcoLogo.url} alt="Yootelco logo" className="h-9 w-auto" />
          </Link>

          <nav className="hidden items-center justify-center gap-6 text-[14px] font-semibold text-foreground/90 min-[900px]:flex xl:gap-10">
            {navItems.map((item) =>
              ["Solutions", "Use Cases", "Industries"].includes(item.label) ? (
                <div key={item.label} className="group relative">
                  <button type="button" className="flex items-center gap-1.5 whitespace-nowrap text-foreground/80 transition-colors hover:text-foreground">
                    <span className="relative">
                      {item.label}
                      <span className="pointer-events-none absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-primary transition-all duration-300 group-hover:w-full" />
                    </span>
                    <span className="text-[11px] text-muted-foreground transition-transform duration-200 group-hover:rotate-180">⌄</span>
                  </button>
                  <div className="invisible absolute left-1/2 top-full z-50 w-[420px] -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                    <div className="rounded-2xl border border-border/60 bg-card p-3 shadow-lift">
                      {(item.label === "Solutions" ? solutionsMenu : item.label === "Use Cases" ? useCasesMenu : industriesMenu).map((sol) => (
                        <Link key={sol.title} to={sol.href} className="flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-secondary">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary" aria-hidden="true">
                            <sol.Icon className="h-5 w-5" strokeWidth={2.3} />
                          </span>
                          <div className="min-w-0">
                            <div className="text-[14px] font-bold text-foreground">{sol.title}</div>
                            <p className="mt-0.5 text-[12.5px] font-medium leading-[1.5] text-muted-foreground">{sol.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : item.externalHref ? (

                <a
                  key={item.label}
                  href={item.externalHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative flex items-center gap-1.5 whitespace-nowrap text-foreground/80 transition-colors hover:text-foreground"
                >
                  <span className="relative">
                    {item.label}
                    <span className="pointer-events-none absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-primary transition-all duration-300 group-hover:w-full" />
                  </span>
                </a>
              ) : (
                <Link key={item.label} to={navHref(item.label)} className="group relative flex items-center gap-1.5 whitespace-nowrap text-foreground/80 transition-colors hover:text-foreground">
                  <span className="relative">
                    {item.label}
                    <span className="pointer-events-none absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-primary transition-all duration-300 group-hover:w-full" />
                  </span>
                  {item.hasChevron ? <span className="text-[11px] text-muted-foreground">⌄</span> : null}
                </Link>
              )
            )}
          </nav>

          <div className="min-[900px]:hidden" />

          <Link to="/contact" className="hidden rounded-[6px] bg-primary px-[20px] py-[11px] text-[14px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift min-[900px]:inline-flex">
            Contact Sales
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-[10px] border border-border/70 bg-card/90 text-foreground shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift min-[900px]:hidden" aria-label="Open navigation menu">
                <Menu className="h-6 w-6" strokeWidth={2.4} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] border-border/70 bg-card px-5 pb-6 pt-16 sm:max-w-[380px]">
              <SheetTitle className="sr-only">Mobile navigation</SheetTitle>
              <div className="flex flex-col gap-6">
                <Link to="/" aria-label="Yootelco home" className="flex items-center">
                  <img src={yootelcoLogo.url} alt="Yootelco logo" className="h-9 w-auto" />
                </Link>
                <nav className="flex flex-col gap-2">
                  {navItems.map((item) =>
                    ["Solutions", "Use Cases", "Industries"].includes(item.label) ? (
                      <Accordion key={item.label} type="single" collapsible className="w-full">
                        <AccordionItem value={item.label} className="border-none">
                          <AccordionTrigger className="min-h-[52px] rounded-[10px] px-4 py-0 text-[16px] font-semibold text-foreground hover:bg-secondary hover:no-underline">
                            {item.label}
                          </AccordionTrigger>
                          <AccordionContent className="pb-2 pt-2">
                            <div className="flex flex-col gap-1">
                              {(item.label === "Solutions" ? solutionsMenu : item.label === "Use Cases" ? useCasesMenu : industriesMenu).map((sol) => (
                                <SheetClose asChild key={sol.title}>
                                  <Link to={sol.href} className="flex items-start gap-3 rounded-[10px] p-3 transition-colors hover:bg-secondary">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary" aria-hidden="true">
                                      <sol.Icon className="h-4 w-4" strokeWidth={2.3} />
                                    </span>
                                    <div className="min-w-0">
                                      <div className="text-[14px] font-bold text-foreground">{sol.title}</div>
                                      <p className="mt-0.5 text-[12.5px] leading-[1.5] text-muted-foreground">{sol.description}</p>
                                    </div>
                                  </Link>
                                </SheetClose>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : item.externalHref ? (

                      <SheetClose asChild key={item.label}>
                        <a
                          href={item.externalHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex min-h-[52px] items-center rounded-[10px] px-4 text-[16px] font-semibold text-foreground transition-colors hover:bg-secondary"
                        >
                          {item.label}
                        </a>
                      </SheetClose>
                    ) : (
                      <SheetClose asChild key={item.label}>
                        <Link to={navHref(item.label)} className="flex min-h-[52px] items-center rounded-[10px] px-4 text-[16px] font-semibold text-foreground transition-colors hover:bg-secondary">
                          {item.label}
                        </Link>
                      </SheetClose>
                    )
                  )}
                </nav>
                <SheetClose asChild>
                  <Link to="/contact" className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] bg-primary px-5 text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
                    Contact Sales
                  </Link>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default SiteHeader;
